import React from 'react';
import { useInView } from 'react-intersection-observer';
import { motion } from 'framer-motion';
import { contactContent } from '../../data/portfolio';
import { Mail, SendHorizonal } from 'lucide-react';

const Contact: React.FC = () => {
  const { ref, inView } = useInView({
    threshold: 0.15,
    triggerOnce: true,
  });

  return (
    <section
      id="contact"
      ref={ref}
      className="bg-slate-50/60 py-16 dark:bg-slate-950/60 sm:py-20"
      aria-labelledby="contact-heading"
    >
      <div className="mx-auto grid max-w-6xl gap-8 px-4 sm:gap-10 sm:px-6 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <h2
            id="contact-heading"
            className="text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl md:text-3xl dark:text-slate-50"
          >
            Let’s talk
          </h2>
          <p className="mt-2 max-w-xl text-sm text-slate-600 sm:text-base dark:text-slate-300">
            This form is a placeholder wired for a future email provider (for
            example EmailJS or a small serverless function). For now it just
            logs data in the console so you can test the UX without sending
            real emails.
          </p>

          <form
            className="mt-5 space-y-3.5 sm:mt-6 sm:space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              const data = new FormData(event.currentTarget);
              // Placeholder submit handler – replace with EmailJS or your backend later.
              // eslint-disable-next-line no-console
              console.log('Contact form submission', Object.fromEntries(data.entries()));
              alert('Thanks for your message! In production this would send via EmailJS.');
              event.currentTarget.reset();
            }}
          >
            <div>
              <label
                htmlFor="name"
                className="block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300"
              >
                Name
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                className="mt-1.5 w-full rounded-lg border border-slate-200/80 bg-white/90 px-3 py-2 text-sm text-slate-900 shadow-sm outline-none ring-brand-pink/70 transition focus:ring-2 dark:border-slate-700/80 dark:bg-slate-900/80 dark:text-slate-50"
                placeholder="Your name"
              />
            </div>
            <div>
              <label
                htmlFor="email"
                className="block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300"
              >
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                className="mt-1.5 w-full rounded-lg border border-slate-200/80 bg-white/90 px-3 py-2 text-sm text-slate-900 shadow-sm outline-none ring-brand-pink/70 transition focus:ring-2 dark:border-slate-700/80 dark:bg-slate-900/80 dark:text-slate-50"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label
                htmlFor="message"
                className="block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300"
              >
                Message
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows={4}
                className="mt-1.5 w-full resize-none rounded-lg border border-slate-200/80 bg-white/90 px-3 py-2 text-sm text-slate-900 shadow-sm outline-none ring-brand-pink/70 transition focus:ring-2 dark:border-slate-700/80 dark:bg-slate-900/80 dark:text-slate-50"
                placeholder="Tell me a bit about your project or role..."
              />
            </div>
            <button
              type="submit"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-hero-gradient px-6 py-2.5 text-sm font-semibold text-white shadow-glass transition hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-50 focus-visible:ring-brand-pink/80 dark:focus-visible:ring-offset-slate-950"
            >
              Send message
              <SendHorizonal className="h-4 w-4" />
            </button>
          </form>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, ease: 'easeOut', delay: 0.1 }}
          className="glass-card flex flex-col justify-between bg-glass-light p-4 text-sm sm:p-6 dark:bg-glass-dark"
        >
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-50">
              Direct contact
            </h3>
            <a
              href={`mailto:${contactContent.email}`}
              className="mt-3 inline-flex items-center gap-2 rounded-full bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-100 transition hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
            >
              <Mail className="h-3.5 w-3.5" />
              {contactContent.email}
            </a>
          </div>

          <div className="mt-6">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Social
            </p>
            <div className="mt-2 flex flex-wrap gap-2 text-xs">
              <a
                href={contactContent.socials.linkedin}
                target="_blank"
                rel="noreferrer"
                className="rounded-full bg-slate-900/90 px-3 py-1 text-slate-100 transition hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
              >
                LinkedIn
              </a>
              <a
                href={contactContent.socials.github}
                target="_blank"
                rel="noreferrer"
                className="rounded-full border border-slate-200/80 bg-white/80 px-3 py-1 text-slate-700 transition hover:border-brand-pink/70 hover:text-slate-900 dark:border-slate-700/80 dark:bg-slate-900/80 dark:text-slate-200 dark:hover:border-brand-pink/70"
              >
                GitHub
              </a>
              <a
                href={contactContent.socials.instagram}
                target="_blank"
                rel="noreferrer"
                className="rounded-full border border-slate-200/80 bg-white/80 px-3 py-1 text-slate-700 transition hover:border-brand-pink/70 hover:text-slate-900 dark:border-slate-700/80 dark:bg-slate-900/80 dark:text-slate-200 dark:hover:border-brand-pink/70"
              >
                Instagram
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;



